import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  ArrowRight,
  ChefHat,
  Clock3,
  Flame,
  Leaf,
  Minus,
  Plus,
  ShoppingBag,
  Sparkles,
  Star,
  Utensils,
} from "lucide-react";

import {
  fetchMenu,
} from "../../actions/menuActions";

import {
  useCart,
} from "../../components/CartContext";

import "./css/HomeScreen.css";


/* =========================================================
   DECORATIVE IMAGES

   These are only used for Theme 10 visual sections.
   Actual menu item images still come from the backend.
========================================================= */

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1563379926898-05f4575a45d8?auto=format&fit=crop&w=1400&q=90";

const ABOUT_IMAGE =
  "https://images.unsplash.com/photo-1601050690597-df0568f70950?auto=format&fit=crop&w=1400&q=90";


/* =========================================================
   IMAGE HELPER
========================================================= */

const getImageUrl = (image) => {

  if (!image) {
    return "";
  }

  const imageValue =
    typeof image === "string"
      ? image
      : image.url ||
        image.image ||
        "";

  if (!imageValue) {
    return "";
  }

  if (
    imageValue.startsWith("http://") ||
    imageValue.startsWith("https://")
  ) {
    return imageValue;
  }

  return `${
    imageValue.startsWith("/")
      ? ""
      : "/"
  }${imageValue}`;

};


/* =========================================================
   HOME SCREEN
========================================================= */

function HomeScreen() {

  const dispatch =
    useDispatch();

  const navigate =
    useNavigate();

  const {
    restaurantId,
    tableId,
  } = useParams();


  /* =====================================================
     REDUX MENU
  ===================================================== */

  const menuState =
    useSelector(
      (state) =>
        state.menu || {}
    );


  const {
    categories = [],
    loading = false,
    error = null,
  } = menuState;


  /* =====================================================
     CART
  ===================================================== */

  const {
    cart = [],
    cartCount = 0,
    addToCart,
    increaseQuantity,
    decreaseQuantity,
  } = useCart();


  /* =====================================================
     STATE
  ===================================================== */

  const [
    activeCategory,
    setActiveCategory,
  ] = useState(null);


  const [
    popularPage,
    setPopularPage,
  ] = useState(0);


  /* =====================================================
     FETCH MENU
  ===================================================== */

  useEffect(() => {

    if (!restaurantId) {
      return;
    }

    dispatch(
      fetchMenu(
        restaurantId
      )
    );

  }, [
    dispatch,
    restaurantId,
  ]);


  /* =====================================================
     SORT / CLEAN CATEGORIES
  ===================================================== */

  const sortedCategories =
    useMemo(() => {

      if (
        !Array.isArray(categories)
      ) {
        return [];
      }

      return [...categories]

        .filter(
          (category) => {

            if (
              category?.is_active === false
            ) {
              return false;
            }

            const items =
              category?.items ||
              category?.menu_items ||
              [];

            return (
              Array.isArray(items) &&
              items.some(
                (item) =>
                  item?.is_available !== false
              )
            );

          }
        )

        .map(
          (category) => {

            const items =
              category?.items ||
              category?.menu_items ||
              [];

            return {

              ...category,

              items:
                items
                  .filter(
                    (item) =>
                      item?.is_available !== false
                  )
                  .sort(
                    (a, b) =>
                      Number(
                        a?.display_order || 0
                      ) -
                      Number(
                        b?.display_order || 0
                      )
                  ),

            };

          }
        )

        .sort(
          (a, b) =>
            Number(
              a?.display_order || 0
            ) -
            Number(
              b?.display_order || 0
            )
        );

    }, [
      categories,
    ]);


  /* =====================================================
     ALL MENU ITEMS
  ===================================================== */

  const allItems =
    useMemo(() => {

      return sortedCategories.flatMap(
        (category) => {

          return (
            category.items || []
          ).map(
            (item) => ({

              ...item,

              categoryName:
                category.name,

              categoryId:
                category.id,

            })
          );

        }
      );

    }, [
      sortedCategories,
    ]);


  /* =====================================================
     POPULAR ITEMS

     Prefer explicitly popular / featured items.
     If backend does not provide those flags,
     use the first items as fallback.
  ===================================================== */

  const popularItems =
    useMemo(() => {

      const explicitlyPopular =
        allItems.filter(
          (item) =>
            item?.is_popular === true ||
            item?.is_featured === true ||
            item?.featured === true
        );


      if (
        explicitlyPopular.length >= 3
      ) {

        return explicitlyPopular.slice(
          0,
          12
        );

      }


      return allItems.slice(
        0,
        12
      );

    }, [
      allItems,
    ]);


  /* =====================================================
     POPULAR PAGES

     3 dishes per slide.
  ===================================================== */

  const popularPages =
    useMemo(() => {

      const pages = [];

      for (
        let index = 0;
        index < popularItems.length;
        index += 3
      ) {

        pages.push(
          popularItems.slice(
            index,
            index + 3
          )
        );

      }

      return pages;

    }, [
      popularItems,
    ]);


  /* =====================================================
     RESET POPULAR PAGE
  ===================================================== */

  useEffect(() => {

    setPopularPage(0);

  }, [
    popularPages.length,
  ]);


  /* =====================================================
     MOBILE POPULAR AUTO SLIDE
  ===================================================== */

  useEffect(() => {

    if (
      popularPages.length <= 1
    ) {
      return undefined;
    }


    const interval =
      window.setInterval(
        () => {

          if (
            window.innerWidth <= 650
          ) {

            setPopularPage(
              (currentPage) =>
                (
                  currentPage + 1
                ) %
                popularPages.length
            );

          }

        },
        3500
      );


    return () => {

      window.clearInterval(
        interval
      );

    };

  }, [
    popularPages.length,
  ]);


  // Select the first available category when the menu loads or changes.
  useEffect(() => {
    if (!sortedCategories.length) {
      setActiveCategory(null);
      return;
    }
    setActiveCategory((current) =>
      sortedCategories.some((category) => String(category.id) === String(current))
        ? current
        : sortedCategories[0].id
    );
  }, [sortedCategories]);

  /* =====================================================
     FILTERED MENU
  ===================================================== */

  const filteredCategories =
    useMemo(() => {



      return sortedCategories.filter(
        (category) =>
          String(category.id) ===
          String(activeCategory)
      );

    }, [
      activeCategory,
      sortedCategories,
    ]);


  /* =====================================================
     FIND CART ITEM
  ===================================================== */

  const getCartItem = (
    itemId
  ) => {

    return cart.find(
      (cartItem) =>
        String(cartItem.id) ===
        String(itemId)
    );

  };


  /* =====================================================
     ADD ITEM
  ===================================================== */

  const handleAddToCart = (
    item
  ) => {

    /*
      Keep restaurantId here.

      Theme 9 / CartContext restaurant
      scoping should use the same restaurant.
    */

    addToCart(
      item,
      restaurantId
    );

  };


  /* =====================================================
     INCREASE ITEM
  ===================================================== */

  const handleIncrease = (
    item
  ) => {

    if (
      typeof increaseQuantity ===
      "function"
    ) {

      increaseQuantity(
        item.id
      );

      return;

    }


    handleAddToCart(
      item
    );

  };


  /* =====================================================
     DECREASE ITEM
  ===================================================== */

  const handleDecrease = (
    item
  ) => {

    if (
      typeof decreaseQuantity !==
      "function"
    ) {
      return;
    }


    decreaseQuantity(
      item.id
    );

  };


  /* =====================================================
     PRICE
  ===================================================== */

  const formatPrice = (
    value
  ) => {

    return Number(
      value || 0
    ).toFixed(0);

  };


  /* =====================================================
     SCROLL
  ===================================================== */

  const scrollToSection = (
    sectionId
  ) => {

    const section =
      document.getElementById(
        sectionId
      );


    if (!section) {
      return;
    }


    const headerHeight =
      document.querySelector(
        ".theme10-header"
      )?.offsetHeight || 0;


    const position =
      section
        .getBoundingClientRect()
        .top +
      window.scrollY -
      headerHeight -
      14;


    window.scrollTo({

      top: position,

      behavior: "smooth",

    });

  };


  /* =====================================================
     CART NAVIGATION
  ===================================================== */

  const handleOpenCart = () => {

    navigate(
      "cart"
    );

  };


  /* =====================================================
     RENDER
  ===================================================== */

  return (

    <main className="theme10-home">


      {/* =================================================
          HERO
      ================================================= */}

      <section className="theme10-hero">

        <div className="theme10-container theme10-hero-grid">


          {/* =================================================
              HERO CONTENT
          ================================================= */}

          <div className="theme10-hero-content">


            <div className="theme10-eyebrow">

              <span className="theme10-eyebrow-line">
              </span>

              <span>
                SLOW COOKED · FULL OF SOUL
              </span>

            </div>


            <h1>

              Master

              <span>
                Biryani
              </span>

            </h1>


            <p className="theme10-hero-description">

              Fragrant rice, carefully
              selected spices and rich
              traditional flavours come
              together in every plate.

            </p>


            <div className="theme10-hero-actions">

              <button
                type="button"
                className="theme10-primary-button"
                onClick={() =>
                  scrollToSection(
                    "theme10-menu"
                  )
                }
              >

                Order Now

                <ArrowRight
                  size={17}
                />

              </button>


              <button
                type="button"
                className="theme10-text-button"
                onClick={() =>
                  scrollToSection(
                    "theme10-about"
                  )
                }
              >

                Our Story

              </button>

            </div>


            {/* =================================================
                HERO SMALL INFO
            ================================================= */}

            <div className="theme10-hero-features">


              <div>

                <span>

                  <Clock3
                    size={15}
                  />

                </span>

                <p>

                  <strong>
                    Slow Cooked
                  </strong>

                  <small>
                    Made with patience
                  </small>

                </p>

              </div>


              <div>

                <span>

                  <Leaf
                    size={15}
                  />

                </span>

                <p>

                  <strong>
                    Fresh Spices
                  </strong>

                  <small>
                    Aromatic ingredients
                  </small>

                </p>

              </div>


            </div>


          </div>


          {/* =================================================
              HERO IMAGE
          ================================================= */}

          <div className="theme10-hero-visual">


            <div className="theme10-hero-sage-shape">
            </div>


            <div className="theme10-hero-image-wrap">

              <img
                src={HERO_IMAGE}
                alt="Freshly prepared biryani"
              />

            </div>


            <div className="theme10-hero-badge">

              <span>
                <Flame
                  size={15}
                />
              </span>

              <div>

                <small>
                  CHEF'S
                </small>

                <strong>
                  SPECIAL
                </strong>

              </div>

            </div>


            <div className="theme10-hero-spice">

              <Leaf
                size={19}
              />

            </div>


          </div>


        </div>


        <div className="theme10-hero-decoration theme10-hero-decoration-one">
        </div>

        <div className="theme10-hero-decoration theme10-hero-decoration-two">
        </div>

      </section>


      {/* =================================================
          POPULAR / SIGNATURE DISHES
      ================================================= */}

      <section
        className="theme10-popular"
        id="theme10-popular"
      >

        <div className="theme10-container">


          {/* =================================================
              SECTION HEADER
          ================================================= */}

          <div className="theme10-section-heading theme10-section-heading-center">

            <span className="theme10-section-kicker">

              <Sparkles
                size={13}
              />

              SIGNATURE PLATES

            </span>


            <h2>
              Royal Favourites
            </h2>


            <p>

              Discover the dishes our
              guests come back for.

            </p>

          </div>


          {/* =================================================
              LOADING
          ================================================= */}

          {loading && (

            <div className="theme10-state">

              <div className="theme10-loader">
              </div>

              <p>
                Preparing the menu...
              </p>

            </div>

          )}


          {/* =================================================
              ERROR
          ================================================= */}

          {!loading &&
            error && (

              <div className="theme10-state theme10-state-error">

                <p>

                  {typeof error === "string"
                    ? error
                    : "Unable to load the menu."}

                </p>

              </div>

            )}


          {/* =================================================
              POPULAR SLIDER
          ================================================= */}

          {!loading &&
            !error &&
            popularPages.length > 0 && (

              <div className="theme10-popular-slider">


                <div
                  className="theme10-popular-track"
                  style={{
                    transform:
                      `translateX(-${
                        popularPage * 100
                      }%)`,
                  }}
                >

                  {popularPages.map(
                    (
                      page,
                      pageIndex
                    ) => (

                      <div
                        className="theme10-popular-page"
                        key={`popular-page-${pageIndex}`}
                      >

                        {page.map(
                          (
                            item,
                            itemIndex
                          ) => {

                            const cartItem =
                              getCartItem(
                                item.id
                              );


                            return (

                              <article
                                className="theme10-popular-card"
                                key={
                                  item.id ||
                                  `${pageIndex}-${itemIndex}`
                                }
                              >


                                {/* IMAGE */}

                                <div className="theme10-popular-image">

                                  {getImageUrl(
                                    item.image
                                  ) ? (

                                    <img
                                      src={getImageUrl(
                                        item.image
                                      )}
                                      alt={
                                        item.name
                                      }
                                    />

                                  ) : (

                                    <div className="theme10-image-placeholder">

                                      <Utensils
                                        size={27}
                                      />

                                    </div>

                                  )}


                                  <span className="theme10-popular-number">

                                    {String(
                                      pageIndex * 3 +
                                      itemIndex +
                                      1
                                    ).padStart(
                                      2,
                                      "0"
                                    )}

                                  </span>


                                  <div className="theme10-rating">

                                    <Star
                                      size={10}
                                      fill="currentColor"
                                    />

                                    4.8

                                  </div>

                                </div>


                                {/* CONTENT */}

                                <div className="theme10-popular-content">

                                  <span className="theme10-card-category">

                                    {item.categoryName ||
                                      "SIGNATURE"}

                                  </span>


                                  <h3>
                                    {item.name}
                                  </h3>


                                  {item.description && (

                                    <p>

                                      {
                                        item.description
                                      }

                                    </p>

                                  )}


                                  <div className="theme10-popular-bottom">

                                    <strong className="theme10-price">

                                      ₹
                                      {formatPrice(
                                        item.price
                                      )}

                                    </strong>


                                    {!cartItem ? (

                                      <button
                                        type="button"
                                        className="theme10-add-circle"
                                        onClick={() =>
                                          handleAddToCart(
                                            item
                                          )
                                        }
                                        aria-label={`Add ${item.name} to cart`}
                                      >

                                        <Plus
                                          size={17}
                                        />

                                      </button>

                                    ) : (

                                      <div className="theme10-mini-quantity">

                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleDecrease(
                                              item
                                            )
                                          }
                                        >

                                          <Minus
                                            size={12}
                                          />

                                        </button>


                                        <strong>
                                          {cartItem.quantity}
                                        </strong>


                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleIncrease(
                                              item
                                            )
                                          }
                                        >

                                          <Plus
                                            size={12}
                                          />

                                        </button>

                                      </div>

                                    )}

                                  </div>

                                </div>


                              </article>

                            );

                          }
                        )}

                      </div>

                    )
                  )}

                </div>


                {/* DOTS */}

                {popularPages.length > 1 && (

                  <div className="theme10-popular-dots">

                    {popularPages.map(
                      (
                        _,
                        index
                      ) => (

                        <button
                          type="button"
                          key={`dot-${index}`}
                          className={
                            popularPage === index
                              ? "active"
                              : ""
                          }
                          onClick={() =>
                            setPopularPage(
                              index
                            )
                          }
                          aria-label={`Popular dishes page ${
                            index + 1
                          }`}
                        />

                      )
                    )}

                  </div>

                )}


              </div>

            )}


        </div>

      </section>


      {/* =================================================
          FEATURES STRIP
      ================================================= */}

      <section className="theme10-features">

        <div className="theme10-container">

          <div className="theme10-feature-grid">


            <div className="theme10-feature">

              <span>

                <Leaf
                  size={19}
                />

              </span>

              <div>

                <strong>
                  Fresh Ingredients
                </strong>

                <p>
                  Selected every day
                </p>

              </div>

            </div>


            <div className="theme10-feature">

              <span>

                <ChefHat
                  size={19}
                />

              </span>

              <div>

                <strong>
                  Expert Kitchen
                </strong>

                <p>
                  Crafted with tradition
                </p>

              </div>

            </div>


            <div className="theme10-feature">

              <span>

                <Flame
                  size={19}
                />

              </span>

              <div>

                <strong>
                  Dum Cooked
                </strong>

                <p>
                  Sealed for rich flavour
                </p>

              </div>

            </div>


            <div className="theme10-feature">

              <span>

                <Clock3
                  size={19}
                />

              </span>

              <div>

                <strong>
                  Served Fresh
                </strong>

                <p>
                  Straight from kitchen
                </p>

              </div>

            </div>


          </div>

        </div>

      </section>


      {/* =================================================
          ABOUT
      ================================================= */}

      <section
        className="theme10-about"
        id="theme10-about"
      >

        <div className="theme10-container theme10-about-grid">


          {/* IMAGE */}

          <div className="theme10-about-visual">

            <div className="theme10-about-sage">
            </div>


            <div className="theme10-about-image">

              <img
                src={ABOUT_IMAGE}
                alt="Traditional Indian food"
              />

            </div>


            <div className="theme10-about-badge">

              <strong>
                100%
              </strong>

              <span>
                FRESHLY
                <br />
                PREPARED
              </span>

            </div>

          </div>


          {/* CONTENT */}

          <div className="theme10-about-content">

            <span className="theme10-section-kicker">

              <Leaf
                size={13}
              />

              OUR KITCHEN STORY

            </span>


            <h2>

              Tradition in
              every grain.

            </h2>


            <p>

              Great biryani is more than
              rice and spice. It is time,
              aroma, balance and the care
              that goes into every layer.

            </p>


            <p>

              Our kitchen brings together
              fragrant rice, fresh herbs
              and carefully chosen spices
              to create food worth sharing.

            </p>


            <div className="theme10-about-points">


              <div>

                <span>
                  01
                </span>

                <p>

                  <strong>
                    Carefully Selected
                  </strong>

                  <small>
                    Quality ingredients
                  </small>

                </p>

              </div>


              <div>

                <span>
                  02
                </span>

                <p>

                  <strong>
                    Cooked with Care
                  </strong>

                  <small>
                    Traditional techniques
                  </small>

                </p>

              </div>


            </div>


            <button
              type="button"
              className="theme10-outline-button"
              onClick={() =>
                scrollToSection(
                  "theme10-menu"
                )
              }
            >

              Explore Our Menu

              <ArrowRight
                size={16}
              />

            </button>

          </div>


        </div>

      </section>


      {/* =================================================
          FULL MENU
      ================================================= */}

      <section
        className="theme10-menu"
        id="theme10-menu"
      >

        <div className="theme10-container">


          {/* =================================================
              MENU HEADING
          ================================================= */}

          <div className="theme10-menu-heading">


            <div>

              <span className="theme10-section-kicker">

                <Utensils
                  size={13}
                />

                OUR MENU

              </span>


              <h2>
                Made For Cravings
              </h2>

            </div>


            <p>

              Pick a category and discover
              something delicious.

            </p>


          </div>


          {/* =================================================
              CATEGORY SELECTOR
          ================================================= */}

          {!loading &&
            sortedCategories.length > 0 && (

              <div className="theme10-category-wrap">

                {sortedCategories
                  .map(
                    (category) => (

                      <button
                        type="button"
                        key={
                          category.id
                        }
                        className={
                          String(
                            activeCategory
                          ) ===
                          String(
                            category.id
                          )
                            ? "theme10-category-button active"
                            : "theme10-category-button"
                        }
                        onClick={() =>
                          setActiveCategory(
                            category.id
                          )
                        }
                      >

                        {category.name}

                      </button>

                    )
                  )}

              </div>

            )}


          {/* =================================================
              LOADING
          ================================================= */}

          {loading && (

            <div className="theme10-state">

              <div className="theme10-loader">
              </div>

              <p>
                Loading delicious dishes...
              </p>

            </div>

          )}


          {/* =================================================
              ERROR
          ================================================= */}

          {!loading &&
            error && (

              <div className="theme10-state theme10-state-error">

                <p>

                  {typeof error === "string"
                    ? error
                    : "Unable to load the menu."}

                </p>

              </div>

            )}


          {/* =================================================
              EMPTY MENU
          ================================================= */}

          {!loading &&
            !error &&
            filteredCategories.length ===
              0 && (

              <div className="theme10-state">

                <Utensils
                  size={30}
                />

                <p>
                  No dishes available.
                </p>

              </div>

            )}


          {/* =================================================
              MENU CATEGORY SECTIONS
          ================================================= */}

          {!loading &&
            !error &&
            filteredCategories.map(
              (
                category
              ) => (

                <div
                  className="theme10-menu-category"
                  key={category.id}
                >


                  <div className="theme10-menu-category-title">

                    <div>

                      <span>
                        MENU
                      </span>

                      <h3>
                        {category.name}
                      </h3>

                    </div>


                    <small>

                      {
                        category.items
                          ?.length || 0
                      }

                      {" "}
                      dishes

                    </small>

                  </div>


                  <div className="theme10-menu-grid">

                    {category.items.map(
                      (
                        item,
                        itemIndex
                      ) => {

                        const cartItem =
                          getCartItem(
                            item.id
                          );


                        return (

                          <article
                            className="theme10-menu-card"
                            key={
                              item.id ||
                              itemIndex
                            }
                          >


                            {/* =================================================
                                IMAGE
                            ================================================= */}

                            <div className="theme10-menu-image">

                              {getImageUrl(
                                item.image
                              ) ? (

                                <img
                                  src={getImageUrl(
                                    item.image
                                  )}
                                  alt={
                                    item.name
                                  }
                                />

                              ) : (

                                <div className="theme10-image-placeholder">

                                  <Utensils
                                    size={25}
                                  />

                                </div>

                              )}


                              <span className="theme10-menu-card-number">

                                {String(
                                  itemIndex + 1
                                ).padStart(
                                  2,
                                  "0"
                                )}

                              </span>

                            </div>


                            {/* =================================================
                                CARD BODY
                            ================================================= */}

                            <div className="theme10-menu-card-body">

                              <span className="theme10-card-category">

                                {
                                  category.name
                                }

                              </span>


                              <h4>
                                {item.name}
                              </h4>


                              {item.description && (

                                <p>

                                  {
                                    item.description
                                  }

                                </p>

                              )}


                              <div className="theme10-menu-card-bottom">

                                <strong className="theme10-price">

                                  ₹
                                  {formatPrice(
                                    item.price
                                  )}

                                </strong>


                                {!cartItem ? (

                                  <button
                                    type="button"
                                    className="theme10-menu-add"
                                    onClick={() =>
                                      handleAddToCart(
                                        item
                                      )
                                    }
                                  >

                                    <Plus
                                      size={14}
                                    />

                                    <span>
                                      Add
                                    </span>

                                  </button>

                                ) : (

                                  <div className="theme10-menu-quantity">

                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleDecrease(
                                          item
                                        )
                                      }
                                    >

                                      <Minus
                                        size={12}
                                      />

                                    </button>


                                    <strong>
                                      {
                                        cartItem.quantity
                                      }
                                    </strong>


                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleIncrease(
                                          item
                                        )
                                      }
                                    >

                                      <Plus
                                        size={12}
                                      />

                                    </button>

                                  </div>

                                )}

                              </div>

                            </div>


                          </article>

                        );

                      }
                    )}

                  </div>


                </div>

              )
            )}


        </div>

      </section>


      {/* =================================================
          FINAL CTA
      ================================================= */}

      <section className="theme10-final">

        <div className="theme10-final-decoration">
        </div>


        <div className="theme10-container">

          <div className="theme10-final-card">


            <span>

              <Leaf
                size={18}
              />

            </span>


            <div>

              <small>
                MADE WITH LOVE
              </small>

              <h2>
                Hungry already?
              </h2>

              <p>
                Your next favourite dish
                is waiting.
              </p>

            </div>


            <button
              type="button"
              onClick={() =>
                scrollToSection(
                  "theme10-menu"
                )
              }
            >

              Order Your Meal

              <ArrowRight
                size={17}
              />

            </button>


          </div>

        </div>

      </section>


      {/* =================================================
          FLOATING CART
      ================================================= */}

      {cartCount > 0 && (

        <button
          type="button"
          className="theme10-floating-cart"
          onClick={
            handleOpenCart
          }
          aria-label="Open cart"
        >

          <span className="theme10-floating-cart-icon">

            <ShoppingBag
              size={19}
            />

            <small>
              {cartCount}
            </small>

          </span>


          <span className="theme10-floating-cart-text">

            View Cart

          </span>


          <ArrowRight
            size={16}
          />

        </button>

      )}


    </main>

  );

}


export default HomeScreen;